import {
  IAuthorWithEmail,
  IDependentValue,
  ISubmissionFileSet,
  ISubmissionPreference
} from '@publishing-engine/domain/models';
import { createContext, ReactNode, useContext, useState } from 'react';

export type IKeywordsFormData = {
  keywords: { text: string }[];
};

type ISubmissionMetadata = {
  title: string;
  abstract: string;
  keywords: string[];
  ethicsStatement?: IDependentValue<string>;
  clinicalTrials?: IDependentValue<string>;
  noveltyStatement: string;
  funding?: IDependentValue<
    {
      fundingBody: string;
      grantNumber: string;
    }[]
  >;
};

export type IRevisedSubmissionFormData = IKeywordsFormData & {
  type: string;
  metadata: ISubmissionMetadata;
  submittingAuthor: IAuthorWithEmail & {
    orcid: string;
  };
  correspondingAuthor: IAuthorWithEmail;
  contactAuthor: IAuthorWithEmail;
  fileSet: ISubmissionFileSet;
  coauthorsAgreeToEthics?: boolean;
  coauthorsAgreeToSharing?: boolean;
  preference: ISubmissionPreference;
  draftSubmissionId?: string;
};

const defaultState: IRevisedSubmissionFormData = {
  type: '',
  metadata: {
    title: '',
    abstract: '',
    keywords: [],
    ethicsStatement: undefined,
    clinicalTrials: undefined,
    noveltyStatement: '',
    funding: undefined,
  },
  submittingAuthor: {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    affiliation: '',
    orcid: ''
  },
  correspondingAuthor: {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    affiliation: '',
    ringgoldId: ''
  },
  contactAuthor: {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    affiliation: '',
    ringgoldId: ''
  },
  keywords: [],
  fileSet: {
    Manuscript: {
      referenceName: 'Manuscript',
      name: '',
      extension: '',
      url: ''
    }
  },
  preference: {
    dataAvailability: undefined,
    immediatePublication: undefined,
    transparentPeerReview: undefined,
    doubleAnonymousPeerReview: undefined,
    publishingModel: undefined,
    recommendedReviewers: undefined,
    opposedReviewers: undefined,
  }
};

const RevisionFormContext = createContext<{
  formData: IRevisedSubmissionFormData;
  updateFormData: (data: Partial<IRevisedSubmissionFormData>) => void;
  resetFormData: () => void;
}>({
  formData: defaultState,
  updateFormData: () => {},
  resetFormData: () => {}
});

export const useRevisionFormContext = () => {
  const context = useContext(RevisionFormContext);
  if (!context) {
    throw new Error(
      'useRevisionFormContext must be used within a FormProvider'
    );
  }

  return context;
};

export const RevisionFormProvider = ({ children }: { children: ReactNode }) => {
  const [formData, setFormData] =
    useState<IRevisedSubmissionFormData>(defaultState);

  const resetFormData = () => {
    setFormData(defaultState);
  };

  const updateFormData = (updatedData: any) => {
    setFormData(prevData => ({ ...prevData, ...updatedData }));
  };

  return (
    <RevisionFormContext.Provider
      value={{
        formData,
        updateFormData,
        resetFormData
      }}
    >
      {children}
    </RevisionFormContext.Provider>
  );
};
